import * as React from "react";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

export type FormFieldContextValue = {
  id: string;
  helperId: string;
  errorId: string;
  hasError: boolean;
  hasHelper: boolean;
  required: boolean;
  describedBy: string | undefined;
};

export const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

export function useFormField(): FormFieldContextValue {
  const ctx = React.useContext(FormFieldContext);
  if (!ctx) {
    throw new Error("useFormField must be used inside <FormField>");
  }
  return ctx;
}

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  htmlFor?: string;
  helper?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
}

const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ className, label, htmlFor, helper, error, required = false, children, ...props }, ref) => {
    const generatedId = React.useId();
    const id = htmlFor ?? generatedId;
    const helperId = `${id}-helper`;
    const errorId = `${id}-error`;
    const hasError = error != null && error !== false;
    const hasHelper = helper != null && helper !== false;
    const describedBy =
      [hasError ? errorId : null, hasHelper ? helperId : null].filter(Boolean).join(" ") || undefined;

    const contextValue = React.useMemo<FormFieldContextValue>(
      () => ({ id, helperId, errorId, hasError, hasHelper, required, describedBy }),
      [id, helperId, errorId, hasError, hasHelper, required, describedBy],
    );

    return (
      <FormFieldContext.Provider value={contextValue}>
        {/* `content-start` packs the label/control/helper rows to the top. Without it,
            when a grid row stretches this field to match a taller sibling (e.g. one
            with helper text), align-content:stretch would spread the slack between
            the rows and push the control down, breaking baseline alignment. */}
        <div ref={ref} className={cn("grid min-w-0 content-start gap-[6px]", className)} {...props}>
          <Label htmlFor={id}>
            {label}
            {required ? (
              <>
                <span aria-hidden="true"> *</span>
                <span className="sr-only"> (required)</span>
              </>
            ) : null}
          </Label>
          {children}
          {hasHelper && !hasError ? (
            <p id={helperId} className="text-sm text-[color:var(--text-tertiary)]">
              {helper}
            </p>
          ) : null}
          {hasError ? (
            <p id={errorId} role="alert" className="text-sm text-[color:var(--text-error)]">
              {error}
            </p>
          ) : null}
        </div>
      </FormFieldContext.Provider>
    );
  },
);
FormField.displayName = "FormField";

export { FormField };
